import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Save, Bell, Check, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

export default function DcNotificationSettings() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [allRes, recRes] = await Promise.all([
          apiClient.get('/admin/users'),
          apiClient.get('/admin/dc-notification-recipients'),
        ]);
        setUsers(allRes.data.users || []);
        setSelected(new Set((recRes.data.user_ids || [])));
      } catch (e) {
        toast.error('Failed to load users / current recipients');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = [...users].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (!q) return list;
    return list.filter((u) =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q) ||
      (u.user_id || '').toLowerCase().includes(q));
  }, [users, search]);

  const selectedUsers = useMemo(
    () => users.filter((u) => selected.has(u.user_id)),
    [users, selected]);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put('/admin/dc-notification-recipients', { user_ids: [...selected] });
      toast.success(`Saved — ${selected.size} recipient(s) will be alerted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 md:px-6 h-16 flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10"
            onClick={() => navigate('/admin/dashboard')} data-testid="dcn-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Bell className="w-5 h-5 text-fuchsia-400" />
          <div>
            <h1 className="text-lg font-bold">DC Alert Recipients</h1>
            <p className="text-xs text-gray-300">Who gets the Pick &amp; Pack "create DC &amp; transport" message</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-5">
        <div className="bg-white border border-[#E5DFD0] rounded-xl p-4 text-sm text-[#5A5F6E]">
          When a <b>Pick &amp; Pack</b> slip is fully packed, the people selected below get an automatic
          direct message from <b>WMS System</b> in the Internal Messenger asking them to create the DC number and assign transport.
        </div>

        {/* selected chips */}
        <div className="bg-white border border-[#E5DFD0] rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-[#1A1F2E]">Selected recipients</span>
            <Badge className="bg-[#2D5F4F]" data-testid="dcn-selected-count">{selected.size}</Badge>
          </div>
          {selectedUsers.length === 0 ? (
            <p className="text-sm text-gray-400">None yet — pick users below.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="dcn-selected-chips">
              {selectedUsers.map((u) => (
                <button key={u.user_id} type="button" onClick={() => toggle(u.user_id)}
                  className="inline-flex items-center gap-1 text-xs bg-[#2D5F4F]/10 text-[#2D5F4F] border border-[#2D5F4F]/30 rounded-full px-2 py-1 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                  data-testid={`dcn-chip-${u.user_id}`}>
                  {u.name} <X className="w-3 h-3" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* search + user list */}
        <div className="bg-white border border-[#E5DFD0] rounded-xl overflow-hidden">
          <div className="p-3 border-b border-[#E5DFD0] flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or role…" className="border-0 shadow-none focus-visible:ring-0 p-0 h-7"
              data-testid="dcn-search" />
          </div>
          <div className="max-h-[48vh] overflow-y-auto divide-y divide-[#F0EADF]">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-400">No users found</div>
            ) : filtered.map((u) => {
              const on = selected.has(u.user_id);
              return (
                <button key={u.user_id} type="button" onClick={() => toggle(u.user_id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${on ? 'bg-[#2D5F4F]/5' : 'hover:bg-gray-50'}`}
                  data-testid={`dcn-user-${u.user_id}`}>
                  <div className={`w-5 h-5 rounded border flex items-center justify-center ${on ? 'bg-[#2D5F4F] border-[#2D5F4F]' : 'border-gray-300'}`}>
                    {on && <Check className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A1F2E] truncate">{u.name}</p>
                    <p className="text-xs text-gray-500 truncate">{u.role}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving || loading}
            className="bg-[#2D5F4F] hover:bg-[#234A3D] h-11 px-6" data-testid="dcn-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save recipients
          </Button>
        </div>
      </main>
    </div>
  );
}
